import React, { useEffect, useRef, useState } from 'react';
import './styles/clickEffects.css';

const MARKET_DATA = [
  {
    name: 'Rice (local)',
    color: '#2d6a4f',
    unit: 'per 50kg bag',
    base: 61000,
    lo: 54000,
    hi: 72000,
    momPct: -2.1,
    volatility: 0.008,
    trend: 'flat',
    markets: [
      { city: 'Dawanau, Kano',   price: 54000, note: 'Wholesale. Cheapest in North.' },
      { city: 'Bodija, Ibadan',  price: 62000, note: 'Retail bag price.' },
      { city: 'Mile 12, Lagos',  price: 68000, note: 'Logistics premium.' },
      { city: 'Oyingbo, Lagos',  price: 70000, note: 'Retail market.' },
      { city: 'Umuahia, Abia',   price: 65000, note: 'South-East rate.' },
      { city: 'Mile 3, Rivers',  price: 66000, note: 'Port Harcourt rate.' },
      { city: 'Wuse, Abuja',     price: 64000, note: 'FCT average.' },
    ],
  },
  {
    name: 'Beans (white)',
    color: '#c1121f',
    unit: 'per 100kg bag',
    base: 90000,
    lo: 72000,
    hi: 110000,
    momPct: -3.5,
    volatility: 0.01,
    trend: 'flat',
    markets: [
      { city: 'Dawanau, Kano',      price: 72000,  note: 'Cheapest — close to farms.' },
      { city: 'Tudun Wada, Kaduna', price: 76000,  note: 'Northern rate.' },
      { city: 'Mile 12, Lagos',     price: 90000,  note: 'Apr 2026 verified.' },
      { city: 'Bodija, Ibadan',     price: 90000,  note: 'Apr 2026 verified.' },
      { city: 'Igbudu, Delta',      price: 90000,  note: 'Apr 2026 verified.' },
      { city: 'Umuahia, Abia',      price: 95000,  note: 'South-East premium.' },
      { city: 'Ogbogonogo, Delta',  price: 92000,  note: 'Delta market rate.' },
    ],
  },
  {
    name: 'Maize',
    color: '#e07c00',
    unit: 'per 100kg bag',
    base: 45000,
    lo: 32000,
    hi: 58000,
    momPct: 1.8,
    volatility: 0.012,
    trend: 'up',
    markets: [
      { city: 'Dawanau, Kano',      price: 32000,  note: 'Post-harvest low.' },
      { city: 'Tudun Wada, Kaduna', price: 35000,  note: 'Northern wholesale.' },
      { city: 'Bodija, Ibadan',     price: 48000,  note: 'South-West retail.' },
      { city: 'Mile 12, Lagos',     price: 52000,  note: 'Lagos premium.' },
      { city: 'Umuahia, Abia',      price: 50000,  note: 'South-East rate.' },
      { city: 'Wuse, Abuja',        price: 45000,  note: 'FCT average.' },
    ],
  },
  {
    name: 'Garri',
    color: '#3a7bd5',
    unit: 'per paint bucket (~3kg)',
    base: 1900,
    lo: 1200,
    hi: 2800,
    momPct: 2.5,
    volatility: 0.015,
    trend: 'up',
    markets: [
      { city: 'Umuahia, Abia',    price: 1400,  note: 'Garri belt — cheapest.' },
      { city: 'Eke Onunwa, Imo',  price: 1500,  note: 'Imo rate.' },
      { city: 'Ogbogonogo, Delta',price: 1600,  note: 'Delta market.' },
      { city: 'Mile 3, Rivers',   price: 1800,  note: 'Port Harcourt rate.' },
      { city: 'Wuse, Abuja',      price: 1900,  note: 'FCT average.' },
      { city: 'Bodija, Ibadan',   price: 2000,  note: 'Transport costs.' },
      { city: 'Mile 12, Lagos',   price: 2500,  note: 'Lagos premium.' },
    ],
  },
  {
    name: 'Palm Oil',
    color: '#8e44ad',
    unit: 'per 5 litre',
    base: 5200,
    lo: 4000,
    hi: 8500,
    momPct: -1.2,
    volatility: 0.009,
    trend: 'dn',
    markets: [
      { city: 'Eke Onunwa, Imo',   price: 4000,  note: 'Palm belt — producing state.' },
      { city: 'Mile 3, Rivers',    price: 4200,  note: 'Niger Delta rate.' },
      { city: 'Ogbogonogo, Delta', price: 4500,  note: 'Delta market.' },
      { city: 'Umuahia, Abia',     price: 4800,  note: 'Abia rate.' },
      { city: 'Wuse, Abuja',       price: 6000,  note: 'FCT rate.' },
      { city: 'Bodija, Ibadan',    price: 6500,  note: 'South-West rate.' },
      { city: 'Dawanau, Kano',     price: 8200,  note: 'Far North — transport cost.' },
    ],
  },
];

const HISTORY = {
  '1W': { labels: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'], shape: [0,-0.4,-0.2,0.3,0.6,0.8,0.2] },
  '1M': { labels: ['W1 Apr','W2 Apr','W3 Apr','W4 Apr'], shape: [-1.2,-0.3,0.5,1.4] },
  '3M': { labels: ['Feb','Mar','Apr'], shape: [-3.5,-1.2,1.4] },
  '6M': { labels: ['Nov','Dec','Jan','Feb','Mar','Apr'], shape: [4.0,8.0,-15.0,-18.0,-12.0,-10.0] },
};

function generateLine(commodity, tabKey) {
  const hist = HISTORY[tabKey];
  return hist.shape.map(offset => {
    const pctOffset = offset / 100;
    const noise = (Math.random() - 0.5) * commodity.volatility * 2;
    return Math.round(commodity.base * (1 + pctOffset + noise));
  });
}

function MarketPanel({ commodity }) {
  if (!commodity) return null;
  const sorted = [...commodity.markets].sort((a, b) => a.price - b.price);
  const min = sorted[0].price;
  const max = sorted[sorted.length - 1].price;
  return (
    <div style={{ marginTop: 12, background: '#f8fafc', borderRadius: 10, padding: '0.9rem', border: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>
        Price Across Markets — <span style={{ color: '#2d6a4f' }}>{commodity.name}</span>
        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 400, color: '#64748b' }}>({commodity.unit})</span>
      </div>
      <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 10 }}>
        Source: NBS Food Price Watch + market surveys · April 2026
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sorted.map((m, i) => {
          const barPct = ((m.price - min) / (max - min + 1)) * 100;
          const isCheapest = i === 0;
          const isMostExpensive = i === sorted.length - 1;
          return (
            <div key={m.city} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{ width: 110, fontSize: 10, fontWeight: 600, color: '#334155', textAlign: 'right', flexShrink: 0, lineHeight: 1.3 }}>
                {m.city}
              </div>
              <div style={{ flex: 1, background: '#e2e8f0', borderRadius: 4, height: 18, position: 'relative', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.max(8, barPct)}%`, background: isCheapest ? '#2563eb' : isMostExpensive ? '#dc2626' : '#2d6a4f', borderRadius: 4, transition: 'width 0.8s ease' }} />
                <div style={{ position: 'absolute', left: `${Math.min(Math.max(8, barPct) + 1, 55)}%`, top: '50%', transform: 'translateY(-50%)', fontSize: 9, fontWeight: 700, color: '#1e293b', background: 'rgba(255,255,255,0.92)', padding: '1px 3px', borderRadius: 3, whiteSpace: 'nowrap' }}>
                  ₦{m.price.toLocaleString()}
                </div>
              </div>
              {isCheapest && <span style={{ fontSize: 9, fontWeight: 700, color: '#2563eb', flexShrink: 0 }}>Cheapest</span>}
              {isMostExpensive && <span style={{ fontSize: 9, fontWeight: 700, color: '#dc2626', flexShrink: 0 }}>Highest</span>}
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 8, padding: '5px 9px', background: '#f0fdf4', borderRadius: 6, fontSize: 10, color: '#166534' }}>
        Spread: ₦{min.toLocaleString()} – ₦{max.toLocaleString()} · Difference: ₦{(max - min).toLocaleString()} ({(((max - min) / min) * 100).toFixed(0)}%)
      </div>
    </div>
  );
}

export default function PriceChart({ activeTab = '1W', commodity = null, height = 200 }) {
  const [lines, setLines]                   = useState({});
  const [hoveredIdx, setHoveredIdx]         = useState(null);
  const [selectedCommodity, setSelectedCommodity] = useState(null);
  const [dataSource, setDataSource]         = useState('');
  const svgRef = useRef(null);

  const hist     = HISTORY[activeTab] || HISTORY['1W'];
  const labels   = hist.labels;
  const nPoints  = labels.length;

  const filteredCommodities = commodity
    ? MARKET_DATA.filter(c => c.name === commodity)
    : MARKET_DATA;

  useEffect(() => {
    const newLines = {};
    filteredCommodities.forEach(c => { newLines[c.name] = generateLine(c, activeTab); });
    setLines(newLines);
    const sourceMap = {
      '1W': 'Daily estimates · NBS Apr 2026 weekly trend',
      '1M': 'Weekly estimates · NBS Apr 2026 CPI +4.17% MoM',
      '3M': 'NBS Food Price Watch Feb–Apr 2026',
      '6M': 'NBS/WFP data Nov 2025–Apr 2026',
    };
    setDataSource(sourceMap[activeTab] || '');
  }, [activeTab]);

  useEffect(() => {
    const id = setInterval(() => {
      setLines(prev => {
        const next = { ...prev };
        filteredCommodities.forEach(c => {
          if (!next[c.name]) return;
          const arr = [...next[c.name]];
          const biasDrift = (c.momPct / 100) * c.base * 0.002;
          const noise = (Math.random() - 0.5) * c.base * c.volatility;
          const newLast = Math.max(c.lo * 0.9, Math.min(c.hi * 1.1, arr[arr.length - 1] + biasDrift + noise));
          arr[arr.length - 1] = Math.round(newLast);
          next[c.name] = arr;
        });
        return next;
      });
    }, 3500);
    return () => clearInterval(id);
  }, [activeTab]);

  const W = 860, H = height;
  const PAD = { l: 50, r: 20, t: 16, b: 32 };
  const chartW = W - PAD.l - PAD.r;
  const chartH = H - PAD.t - PAD.b;

  const allVals = Object.values(lines).flat();
  const minV = allVals.length ? Math.min(...allVals) * 0.92 : 0;
  const maxV = allVals.length ? Math.max(...allVals) * 1.06 : 100000;

  const scaleX = (i) => PAD.l + (i / Math.max(nPoints - 1, 1)) * chartW;
  const scaleY = (v) => PAD.t + chartH - ((v - minV) / (maxV - minV + 1)) * chartH;

  const fmt = (v) => {
    if (v >= 1000000) return '₦' + (v / 1000000).toFixed(1) + 'M';
    if (v >= 1000)    return '₦' + (v / 1000).toFixed(0) + 'k';
    return '₦' + Math.round(v);
  };

  const yTicks = 5;
  const yStep = (maxV - minV) / yTicks;

  return (
    <div style={{ position: 'relative', width: '100%' }}>

      {/* Data source badge */}
      {dataSource && (
        <div style={{ fontSize: 9, color: '#94a3b8', marginBottom: 4, fontFamily: 'monospace', lineHeight: 1.4 }}>
          {dataSource}
        </div>
      )}

      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H + 10}`}
        width="100%"
        style={{ display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setHoveredIdx(null)}
      >
        {Array.from({ length: yTicks + 1 }, (_, i) => {
          const v = minV + yStep * i;
          const y = scaleY(v);
          return (
            <g key={i}>
              <line x1={PAD.l} y1={y} x2={W - PAD.r} y2={y} stroke="#e8e8e2" strokeWidth="1" strokeDasharray={i === 0 ? 'none' : '3,3'} />
              <text x={PAD.l - 5} y={y + 4} fontSize="9" fill="#a0aec0" fontFamily="monospace" textAnchor="end">{fmt(v)}</text>
            </g>
          );
        })}

        {labels.map((l, i) => (
          <text key={l} x={scaleX(i)} y={H + 8} fontSize="9" fill="#a0aec0" fontFamily="monospace" textAnchor="middle">{l}</text>
        ))}

        {hoveredIdx !== null && (
          <line x1={scaleX(hoveredIdx)} y1={PAD.t} x2={scaleX(hoveredIdx)} y2={H - PAD.b} stroke="rgba(100,116,139,0.3)" strokeWidth="1" strokeDasharray="4,3" />
        )}

        {filteredCommodities.map(c => {
          const pts = lines[c.name];
          if (!pts || pts.length < 2) return null;
          const polyPoints = pts.map((v, i) => `${scaleX(i)},${scaleY(v)}`).join(' ');
          const firstX = scaleX(0), lastX = scaleX(pts.length - 1);
          const baseY = H - PAD.b;
          const areaPoints = `${firstX},${baseY} ${polyPoints} ${lastX},${baseY}`;
          return (
            <g key={c.name}>
              <polygon points={areaPoints} fill={c.color} opacity={0.06} />
              <polyline points={polyPoints} fill="none" stroke={c.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" style={{ transition: 'all 0.5s ease' }} />
              {pts.map((v, i) => (
                <circle key={i} cx={scaleX(i)} cy={scaleY(v)} r={hoveredIdx === i ? 6 : 3.5} fill={hoveredIdx === i ? '#fff' : c.color} stroke={c.color} strokeWidth={hoveredIdx === i ? 2.5 : 0} style={{ cursor: 'pointer', transition: 'r 0.15s' }} onMouseEnter={() => setHoveredIdx(i)} />
              ))}
              {(() => {
                const lastV = pts[pts.length - 1];
                const lx = scaleX(pts.length - 1);
                const ly = scaleY(lastV);
                return (
                  <g>
                    <rect x={lx + 5} y={ly - 10} width={60} height={15} rx={3} fill={c.color} opacity={0.92} />
                    <text x={lx + 35} y={ly + 2} fontSize="9" fill="#fff" fontFamily="monospace" fontWeight="700" textAnchor="middle">{fmt(lastV)}</text>
                  </g>
                );
              })()}
            </g>
          );
        })}

        {hoveredIdx !== null && filteredCommodities.map(c => {
          const pts = lines[c.name];
          if (!pts) return null;
          const v = pts[hoveredIdx];
          const cx = scaleX(hoveredIdx);
          const cy = scaleY(v);
          const ttX = cx > W - 140 ? cx - 80 : cx + 10;
          return (
            <g key={c.name + '-tip'}>
              <rect x={ttX} y={cy - 13} width={65} height={18} rx={4} fill={c.color} opacity={0.95} />
              <text x={ttX + 32} y={cy + 2} fontSize="9" fill="#fff" fontFamily="monospace" fontWeight="700" textAnchor="middle">{fmt(v)}</text>
            </g>
          );
        })}
      </svg>

      {/* ── Legend: always 2-column grid, never wraps ── */}
      {!commodity && (
        <div style={{ marginTop: 10 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 4,
          }}>
            {MARKET_DATA.map(c => {
              const lastPts = lines[c.name];
              const lastVal = lastPts ? lastPts[lastPts.length - 1] : c.base;
              const isSelected = selectedCommodity?.name === c.name;
              const trendCol = c.momPct > 0 ? '#dc2626' : c.momPct < 0 ? '#2563eb' : '#64748b';
              const trendSym = c.momPct > 0 ? '▲' : c.momPct < 0 ? '▼' : '—';
              return (
                <button
                  key={c.name}
                  onClick={() => setSelectedCommodity(isSelected ? null : c)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    background: isSelected ? `${c.color}18` : 'transparent',
                    border: isSelected ? `1.5px solid ${c.color}` : '1.5px solid transparent',
                    borderRadius: 20, padding: '5px 8px', cursor: 'pointer',
                    transition: 'all 0.2s', textAlign: 'left', minWidth: 0,
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.color, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                  <span style={{ fontSize: 10, fontFamily: 'monospace', color: trendCol, fontWeight: 700, flexShrink: 0, marginLeft: 'auto' }}>
                    {trendSym} {fmt(lastVal)}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 5, textAlign: 'center' }}>
            Click commodity to see prices across markets
          </div>
        </div>
      )}

      {/* Market breakdown panel */}
      {!commodity && selectedCommodity && (
        <MarketPanel commodity={selectedCommodity} />
      )}

      {/* ── Price cards: always 2-column grid ── */}
      <div style={{
        marginTop: 12,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 8,
      }}>
        {filteredCommodities.map(c => {
          const pts = lines[c.name];
          const cur = pts ? pts[pts.length - 1] : c.base;
          const trendCol   = c.momPct > 0 ? '#dc2626' : '#2563eb';
          const trendLabel = c.momPct > 0
            ? `+${c.momPct.toFixed(1)}% MoM — rising`
            : `${c.momPct.toFixed(1)}% MoM — falling`;
          return (
            <div key={c.name} style={{
              background: '#f8fafc', borderRadius: 8,
              padding: '9px 11px', border: '1px solid #e2e8f0',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#334155', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
              <div style={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{fmt(cur)}</div>
              <div style={{ fontSize: 9, color: '#64748b', marginTop: 1 }}>{c.unit}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 9 }}>
                <span style={{ color: '#2563eb' }}>Low: {fmt(c.lo)}</span>
                <span style={{ color: '#dc2626' }}>High: {fmt(c.hi)}</span>
              </div>
              <div style={{ fontSize: 9, fontWeight: 700, color: trendCol, marginTop: 3 }}>{trendLabel}</div>
            </div>
          );
        })}
      </div>

      {/* Source footnote */}
      <div style={{ marginTop: 8, fontSize: 9, color: '#cbd5e1', lineHeight: 1.5 }}>
        Prices anchored to NBS Selected Food Price Watch, WFP/HDX Nigeria dataset (updated Apr 2026), NigerianQueries.com Apr 2026 survey, and Independent.ng Apr 2026 market report.
        Live fluctuation uses real NBS month-on-month volatility per commodity. Not a substitute for direct market survey.
      </div>
    </div>
  );
}